import { ButtonBase, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useParams } from "react-router-dom";

import { useTaskCountQueries } from "./useTaskCountQueries";
import { useTaskFilterChips } from "./useTaskFilterChips";

export enum TaskFilterType {
  Open = "open",
  Overdue = "overdue",
  Suggested = "suggested",
}

interface FilterChipProps {
  label: string;
  children: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  color: "primary" | "error" | "info";
  loading: boolean;
}

const FilterChip = ({ label, children, isSelected, onClick, color, loading }: FilterChipProps) => {
  const theme = useTheme();
  const palette = theme.palette[color];

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        px: 2,
        py: 1,
        borderRadius: 1,
        backgroundColor: isSelected ? `${palette.light}20` : theme.palette.grey50.main,
        minWidth: 140,
        "&:hover": {
          backgroundColor: isSelected ? `${palette.light}30` : theme.palette.grey50.dark,
        },
      }}
    >
      <Typography variant="caption">{label}</Typography>
      <Typography variant="body1">
        {loading ? <Skeleton variant="text" width={100} height={20} /> : children}
      </Typography>
    </ButtonBase>
  );
};

export const TaskFilterChips = () => {
  const { activeFilter, handleFilterClick } = useTaskFilterChips();
  const { tenantId } = useParams();
  const { featureFlags } = useFeatureFlags();
  const isSuggestedTasksEnabled = featureFlags[FeatureFlagKey.SuggestedTasks];
  const { openQuery, overdueQuery, suggestedQuery } = useTaskCountQueries(tenantId ?? "");

  return (
    <Stack direction="row" spacing={1.5} sx={{ mb: 2, ml: 3 }}>
      <FilterChip
        label="Open Tasks (Current Year)"
        isSelected={activeFilter === TaskFilterType.Open}
        onClick={() => {
          handleFilterClick(TaskFilterType.Open);
          openQuery.refetch();
        }}
        color="primary"
        loading={openQuery.loading}
      >
        📋 {openQuery.data?.virtualEvents.count ?? 0}
      </FilterChip>
      <FilterChip
        label="Overdue Tasks"
        isSelected={activeFilter === TaskFilterType.Overdue}
        onClick={() => {
          handleFilterClick(TaskFilterType.Overdue);
          overdueQuery.refetch();
        }}
        color="error"
        loading={overdueQuery.loading}
      >
        🖍️ {overdueQuery.data?.virtualEvents.count ?? 0}
      </FilterChip>
      {isSuggestedTasksEnabled && (
        <FilterChip
          label="Suggested Tasks"
          isSelected={activeFilter === TaskFilterType.Suggested}
          onClick={() => {
            handleFilterClick(TaskFilterType.Suggested);
            suggestedQuery.refetch();
          }}
          color="info"
          loading={suggestedQuery.loading}
        >
          <SparkleIcon /> {suggestedQuery.data?.suggestedTasks.count ?? 0}
        </FilterChip>
      )}
    </Stack>
  );
};
